import {
  QueryObserverResult,
  UseQueryOptions,
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useGlobalStore } from '../../store/global-store/global-store';
import {
  bulkUpsertAgentSpending,
  getAgentSpending,
  getAllocationOfCostsChartData,
  getSpending,
} from './financials-client';
import type {
  AgentSpending,
  AgentSpendingBulkUpsertRequest,
  AllocationOfCostsChartRequest,
  AllocationOfCostsChartResponseByCategory,
  Spending,
} from './financials-client.type';

// Query Keys
const AGENT_SPENDING_QUERY_KEY = 'agent-spending';

/**
 * Hook to fetch the allocation of cost measures data
 *
 * @param {string} portfolioId id to fetch the cost measures
 * @param {string} teamId id to fetch the cost measures
 * @param {string} subprojectId id to fetch the cost measures
 * @param {string} workPeriodIds id to fetch the cost measures
 * @param {Date} startDate date to fetch the cost measures
 * @param {Date} endDate date to fetch the cost measures
 * @param options
 * @param successCallback
 * @returns {Object} Object containing the allocation of cost details
 * data and query result
 */
const useAllocationOfCostsMeasures = (
  { portfolioId, teamId, subprojectId, startDate, endDate, workPeriodIds }: AllocationOfCostsChartRequest,
  options?: UseQueryOptions<AllocationOfCostsChartResponseByCategory>,
  successCallback?: (data: AllocationOfCostsChartResponseByCategory) => void,
): {
  chartData: AllocationOfCostsChartResponseByCategory | undefined;
  query: UseQueryResult<AllocationOfCostsChartResponseByCategory>;
} => {
  const superuserMode = useGlobalStore((state) => state.superuserMode);
  const forceRecalc = superuserMode.enabled ? superuserMode.forceRecalculation : false;

  const query = useQuery({
    queryKey: [
      'allocation-of-costs',
      portfolioId,
      teamId,
      subprojectId,
      startDate,
      endDate,
      workPeriodIds,
      forceRecalc,
    ],
    queryFn: () =>
      (portfolioId || teamId) && startDate && endDate
        ? getAllocationOfCostsChartData(
            portfolioId,
            startDate,
            endDate,
            teamId,
            subprojectId,
            workPeriodIds,
            forceRecalc,
          )
        : Promise.reject('Unable to fetch allocation of costs; missing required parameters'),
    ...options,
  });

  if (query.isSuccess && query.data && successCallback) {
    successCallback(query.data);
  }

  return { chartData: query.data, query };
};

/**
 * Hook for fetching the spending data given the year and portfolioID
 *
 * @param {string} portfolioId to fetch the spending for
 * @param {number} year data for the selected year
 * @returns {Object} Object containing the spending data and query result
 */
const useSpending = ({
  portfolioId,
  year,
  options,
}: {
  portfolioId: string;
  year: number;
  options?: Partial<UseQueryOptions<Spending[]>>;
}): { data: Spending[] | undefined; query: QueryObserverResult<Spending[]> } => {
  const query = useQuery({
    queryKey: ['getSpending', portfolioId, year],
    queryFn: () => getSpending(portfolioId as string, year.toString()),
    ...options,
  });

  return { data: query.data, query };
};

/**
 * Hook for fetching agent spending data given the organization ID and years
 *
 * @param {string} organizationId organization ID to fetch the agent spending for
 * @param {number[]} years years for which the data is to be fetched (comma separated)
 * @param {Partial<UseQueryOptions<AgentSpending[]>>} options optional query options
 * @returns {Object} Object containing the agent spending data and query result
 */
const useAgentSpending = ({
  organizationId,
  years,
  options,
}: {
  organizationId: string;
  years?: number[];
  options?: Partial<UseQueryOptions<AgentSpending[]>>;
}): { data: AgentSpending[] | undefined; query: QueryObserverResult<AgentSpending[]> } => {
  const yearsString = years?.join(',');
  const query = useQuery({
    queryKey: [AGENT_SPENDING_QUERY_KEY, organizationId, years],
    queryFn: () => getAgentSpending(organizationId, yearsString),
    enabled: Boolean(organizationId),
    ...options,
  });

  return { data: query.data, query };
};

/**
 * Hook for bulk upserting agent spending data
 *
 * @returns Object containing:
 * - bulkUpsertAgentSpending: Function to bulk upsert agent spending data
 * - isUpserting: Boolean indicating if upsert is in progress
 */
const useBulkUpsertAgentSpending = (): {
  bulkUpsertAgentSpending: (payload: AgentSpendingBulkUpsertRequest) => Promise<AgentSpending[]>;
  isUpserting: boolean;
} => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (payload: AgentSpendingBulkUpsertRequest) => bulkUpsertAgentSpending(payload),
    onSuccess: (_data, variables) => {
      // Invalidate all agent spending queries for the organization
      queryClient.invalidateQueries({
        queryKey: [AGENT_SPENDING_QUERY_KEY, variables.organization_id.toString()],
      });
    },
  });

  return {
    bulkUpsertAgentSpending: (payload: AgentSpendingBulkUpsertRequest) => mutation.mutateAsync(payload),
    isUpserting: mutation.isPending,
  };
};

export { useAgentSpending, useAllocationOfCostsMeasures, useBulkUpsertAgentSpending, useSpending };
